import {
  CompanyProfile,
  CompanyProfileUpdateRequest,
} from "../../../types/company-profile.type";

export type CompanyProfileField = keyof CompanyProfileUpdateRequest;

/** What the form holds while it is edited: every field as text, phone "" when there is none. */
export type CompanyProfileDraft = Record<CompanyProfileField, string>;

export type CompanyProfileErrors = Partial<Record<CompanyProfileField, string>>;

export interface CompanyProfileFieldDefinition {
  field: CompanyProfileField;
  label: string;
  /** Mirrors the column width the API enforces. */
  maxLength: number;
  /** Set for the digit-only fields: exactly this many digits. */
  digitCount?: number;
  isOptional?: boolean;
  helperText?: string;
}

export interface CompanyProfileSection {
  title: string;
  fields: CompanyProfileFieldDefinition[];
}

const NAME_MAX_LENGTH = 255;
const ADDRESS_NO_MAX_LENGTH = 100;
const ROAD_MAX_LENGTH = 255;
const AREA_NAME_MAX_LENGTH = 100;
const BRANCH_NAME_MAX_LENGTH = 100;
const PHONE_MAX_LENGTH = 100;
const PRINTED_PREFIX_HELPER = "พิมพ์ตามที่ต้องการให้แสดงบนเอกสาร รวมคำนำหน้า";

export const COMPANY_PROFILE_SECTIONS: CompanyProfileSection[] = [
  {
    title: "ชื่อบริษัท",
    fields: [
      { field: "name_th", label: "ชื่อบริษัท (ภาษาไทย)", maxLength: NAME_MAX_LENGTH },
      { field: "name_en", label: "ชื่อบริษัท (ภาษาอังกฤษ)", maxLength: NAME_MAX_LENGTH },
    ],
  },
  {
    title: "ที่อยู่",
    fields: [
      { field: "address_no", label: "เลขที่ / อาคาร", maxLength: ADDRESS_NO_MAX_LENGTH },
      {
        field: "road",
        label: "ถนน",
        maxLength: ROAD_MAX_LENGTH,
        helperText: `${PRINTED_PREFIX_HELPER} เช่น ถ.รามคำแหง`,
      },
      {
        field: "subdistrict",
        label: "ตำบล/แขวง",
        maxLength: AREA_NAME_MAX_LENGTH,
        helperText: `${PRINTED_PREFIX_HELPER} เช่น แขวงมีนบุรี`,
      },
      { field: "subdistrict_code", label: "รหัสตำบล/แขวง (DOPA)", maxLength: 6, digitCount: 6 },
      {
        field: "district",
        label: "อำเภอ/เขต",
        maxLength: AREA_NAME_MAX_LENGTH,
        helperText: `${PRINTED_PREFIX_HELPER} เช่น เขตมีนบุรี`,
      },
      { field: "district_code", label: "รหัสอำเภอ/เขต (DOPA)", maxLength: 4, digitCount: 4 },
      { field: "province", label: "จังหวัด", maxLength: AREA_NAME_MAX_LENGTH },
      { field: "province_code", label: "รหัสจังหวัด (DOPA)", maxLength: 2, digitCount: 2 },
      { field: "zipcode", label: "รหัสไปรษณีย์", maxLength: 5, digitCount: 5 },
    ],
  },
  {
    title: "ภาษีและสาขา",
    fields: [
      { field: "tax_id", label: "เลขประจำตัวผู้เสียภาษี", maxLength: 13, digitCount: 13 },
      {
        field: "branch_code",
        label: "รหัสสาขา",
        maxLength: 5,
        digitCount: 5,
        helperText: "สำนักงานใหญ่ใช้ 00000",
      },
      { field: "branch_name", label: "ชื่อสาขา", maxLength: BRANCH_NAME_MAX_LENGTH },
    ],
  },
  {
    title: "ติดต่อ",
    fields: [
      { field: "phone", label: "เบอร์โทร", maxLength: PHONE_MAX_LENGTH, isOptional: true },
    ],
  },
];

const ALL_FIELDS = COMPANY_PROFILE_SECTIONS.flatMap((section) => section.fields);

export const isCompanyProfileField = (name: string): name is CompanyProfileField =>
  ALL_FIELDS.some((definition) => definition.field === name);

/** One value per form field, computed by `toValue` — typed without casting a loose object. */
const mapFields = <T>(
  toValue: (field: CompanyProfileField) => T,
): Record<CompanyProfileField, T> => ({
  name_th: toValue("name_th"),
  name_en: toValue("name_en"),
  address_no: toValue("address_no"),
  road: toValue("road"),
  subdistrict: toValue("subdistrict"),
  subdistrict_code: toValue("subdistrict_code"),
  district: toValue("district"),
  district_code: toValue("district_code"),
  province: toValue("province"),
  province_code: toValue("province_code"),
  zipcode: toValue("zipcode"),
  tax_id: toValue("tax_id"),
  branch_code: toValue("branch_code"),
  branch_name: toValue("branch_name"),
  phone: toValue("phone"),
});

export const toDraft = (profile: CompanyProfile): CompanyProfileDraft =>
  mapFields((field) => profile[field] ?? "");

/** The PATCH body: every field trimmed, a blank phone sent as "no phone". */
export const toUpdateRequest = (
  draft: CompanyProfileDraft,
): CompanyProfileUpdateRequest => {
  const trimmed = mapFields((field) => draft[field].trim());
  return { ...trimmed, phone: trimmed.phone || null };
};

export const isDraftChanged = (
  draft: CompanyProfileDraft,
  profile: CompanyProfile,
): boolean => {
  const stored = toDraft(profile);
  return ALL_FIELDS.some(({ field }) => draft[field].trim() !== stored[field]);
};

const validateField = (
  definition: CompanyProfileFieldDefinition,
  value: string,
): string | null => {
  if (!value) {
    return definition.isOptional ? null : `กรุณากรอก${definition.label}`;
  }
  if (definition.digitCount !== undefined) {
    const isExactDigits = new RegExp(`^\\d{${definition.digitCount}}$`).test(value);
    return isExactDigits ? null : `ต้องเป็นตัวเลข ${definition.digitCount} หลัก`;
  }
  return null;
};

/**
 * The same rules the API applies, so a mistake is shown on its field before saving:
 * required text, exact digit counts, and DOPA codes that nest (district inside province,
 * sub-district inside district).
 */
export const validateDraft = (draft: CompanyProfileDraft): CompanyProfileErrors => {
  const errors: CompanyProfileErrors = {};
  for (const definition of ALL_FIELDS) {
    const message = validateField(definition, draft[definition.field].trim());
    if (message) errors[definition.field] = message;
  }
  const provinceCode = draft.province_code.trim();
  const districtCode = draft.district_code.trim();
  if (!errors.district_code && !errors.province_code && !districtCode.startsWith(provinceCode)) {
    errors.district_code = "รหัสอำเภอ/เขตต้องขึ้นต้นด้วยรหัสจังหวัด";
  }
  if (
    !errors.subdistrict_code &&
    !errors.district_code &&
    !draft.subdistrict_code.trim().startsWith(districtCode)
  ) {
    errors.subdistrict_code = "รหัสตำบล/แขวงต้องขึ้นต้นด้วยรหัสอำเภอ/เขต";
  }
  return errors;
};
